import Icon1 from "@assets/icons/Icon";
import LoadingBar from "@components/ui/LoadingBar";
import React, { useEffect, useState, useRef, JSX, ReactNode } from "react";
import { colors } from "sexy";

export interface SearchOption {
  label: string;
  value: string;
  extra_label?: string;
  extra_label_2?: string;
}

interface SearchAlgoliaProps {
  value?: string;
  setValue: (value: string) => void;
  options?: SearchOption[];
  placeholder?: string;
  onAction: (option: SearchOption | string) => boolean | void;
  classNameCustom?: string;
  start_only?: boolean;
  visible_empty?: boolean;
  show_options?: boolean;
  debounceDelay?: number;
  textfield2t_mode?: boolean;
  focus_on_start?: boolean;
  children?: ReactNode;
  navigateWorkspace?: (path: string) => void;
  /** Async search function — results are shown in the dropdown. */
  onSearch?: (query: string) => Promise<SearchOption[]>;
}

const SearchAlgolia: React.FC<SearchAlgoliaProps> = ({
  value = "",
  setValue,
  options = [],
  placeholder = "Rechercher...",
  onAction,
  classNameCustom = "w-400",
  visible_empty = false,
  show_options = true,
  debounceDelay = 400,
  focus_on_start = false,
  children = null,
  navigateWorkspace = () => {},
  onSearch,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [localInputValue, setLocalInputValue] = useState<string>(value);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isLoading = useRef<boolean>(false);
  const [focus, setFocus] = useState(false);

  // Async search state
  const [isSearching, setIsSearching] = useState(false);
  const [asyncOptions, setAsyncOptions] = useState<SearchOption[]>([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  // Position of the dropdown (fixed, calculated from input rect)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const updateDropdownPos = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  // Sync external value
  useEffect(() => {
    setLocalInputValue(value);
  }, [value]);

  // Debounce: propagate value + call onSearch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (localInputValue !== value || onSearch) {
      isLoading.current = true;
      debounceRef.current = setTimeout(async () => {
        setValue(localInputValue);
        isLoading.current = false;

        if (onSearch) {
          if (localInputValue.trim()) {
            try {
              const results = await onSearch(localInputValue);
              setAsyncOptions(results);
              setIsDropdownVisible(true);
              updateDropdownPos();
            } catch {
              setAsyncOptions([]);
            } finally {
              setIsSearching(false);
            }
          } else {
            setAsyncOptions([]);
            setIsSearching(false);
            setIsDropdownVisible(false);
          }
        }
      }, debounceDelay);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localInputValue, value, setValue, debounceDelay]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalInputValue(val);

    if (onSearch) {
      if (val.trim()) {
        setIsSearching(true);
      } else {
        setIsSearching(false);
        setAsyncOptions([]);
        setIsDropdownVisible(false);
      }
    } else {
      setIsDropdownVisible(!!val || visible_empty);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      isLoading.current = false;
      setValue(localInputValue);
      setIsDropdownVisible(false);
      if (!show_options) onAction("enter");
    }
    if (e.key === "Escape") {
      setIsDropdownVisible(false);
    }
  };

  const resetSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    isLoading.current = false;
    setIsSearching(false);
    setAsyncOptions([]);
    setLocalInputValue("");
    setValue("");
    setIsDropdownVisible(visible_empty);
    if (!visible_empty) onAction({ label: "", value: "" });
  };

  const handleOptionClick = (
    e: React.MouseEvent<HTMLLIElement>,
    option: SearchOption,
  ) => {
    e.stopPropagation();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    isLoading.current = false;
    setLocalInputValue(option.label);
    setValue(option.label);
    const navigated = onAction(option);
    setIsDropdownVisible(false);
    setFocus(false);
    if (!navigated) {
      try {
        navigateWorkspace(`/folder/${option.value}`);
      } catch {}
    }
  };

  const displayedOptions = onSearch ? asyncOptions : options;

  return (
    <>
      <style>{`
        @keyframes algolia-bar {
          0%   { left: -40%; width: 40%; }
          100% { left: 110%; width: 40%; }
        }
      `}</style>

      {/* Backdrop — closes dropdown */}
      {isDropdownVisible && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setIsDropdownVisible(false);
            setFocus(false);
          }}
        />
      )}

      <div ref={containerRef} className={`relative ${classNameCustom}`}>
        {/* Input box */}
        <div
          className={`h-11 flex items-center gap-2 select-none rounded-xl shadow-sm bg-white border text-neutral-400 relative overflow-hidden transition-shadow
            ${focus ? "ring-4 ring-neutral-300" : ""}
          `}
        >
          <svg
            className="w-5 ml-4 h-5 text-neutral-400 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>

          <input
            type="text"
            value={localInputValue}
            onFocus={() => {
              setFocus(true);
              updateDropdownPos();
              if (visible_empty) setIsDropdownVisible(true);
            }}
            onBlur={() => setFocus(false)}
            autoFocus={focus_on_start}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="w-full py-2 text-h2 border-none text-black outline-none bg-transparent"
          />

          {/* Clear button */}
          <div
            className={`${localInputValue ? "opacity-100" : "opacity-0"} flex cursor-pointer h-full items-center pr-3 justify-center shrink-0 transition-opacity`}
            onClick={resetSearch}
          >
            <Icon1 name="effacer2" size={18} color={colors.neutral_400} />
          </div>

          {children && (
            <div className="flex items-center justify-center pr-2">
              {children}
            </div>
          )}

          {/* Blue loading bar — absolute at bottom of input, clipped by overflow-hidden */}
          {isSearching && <LoadingBar variant="absolute" />}
        </div>

        {/* Dropdown — fixed, positioned below the input */}
        {isDropdownVisible && show_options && (
          <ul
            className="fixed z-20 bg-white rounded-xl shadow-xl border border-neutral-100 max-h-72 overflow-y-auto"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              minWidth: dropdownPos.width,
            }}
          >
            {displayedOptions.length > 0
              ? displayedOptions.map((option, index) => (
                  <li
                    key={index}
                    onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                    onClick={(e) => handleOptionClick(e, option)}
                    className="px-4 py-2.5 cursor-pointer hover:bg-neutral-50 flex flex-col gap-0.5 border-b border-neutral-50 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <Icon1
                        name="search"
                        size={18}
                        color={colors.neutral_400}
                      />
                      <span className="text-h2 font-medium">
                        {renderHighlightedText(option.label, localInputValue)}
                      </span>
                    </div>
                    {(option.extra_label || option.extra_label_2) && (
                      <p className="text-h3 text-neutral-400 pl-5">
                        {[option.extra_label, option.extra_label_2]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                  </li>
                ))
              : !isSearching &&
                localInputValue && (
                  <li className="px-3 py-4 text-h3 text-neutral-400">
                    Aucun résultat trouvé
                  </li>
                )}
          </ul>
        )}
      </div>
    </>
  );
};

export default SearchAlgolia;

export const renderHighlightedText = (
  text: string,
  searchValue: string,
): JSX.Element => {
  if (!searchValue.trim()) return <span className="text-gray-700">{text}</span>;

  const lower = text.toLowerCase();
  const queryLower = searchValue.toLowerCase();
  const idx = lower.indexOf(queryLower);

  if (idx === -1) return <span className="text-gray-700">{text}</span>;

  return (
    <span>
      <span className="text-gray-400">{text.substring(0, idx)}</span>
      <span className="text-black font-semibold">
        {text.substring(idx, idx + searchValue.length)}
      </span>
      <span className="text-gray-400">
        {text.substring(idx + searchValue.length)}
      </span>
    </span>
  );
};
